import { showSuccess, showHttpError } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { createAsyncThunk } from '@reduxjs/toolkit'

import {
  destroyDomainControl,
  getDomainControlsList,
  getDomainControlSetting,
  getDomainSummary,
  getOrganizationMemberships,
  patchDomainControlRequest,
  patchDomainControlSetting,
  postDomainControl,
  putVerifyDomainControl,
  postExport
} from './api'
import { VERIFY_DOMAIN_DEBOUNCE_MSEC } from './constants'
import { domainSelector, verificationMethodSelector } from './selectors'

export const getDomainControls = createAsyncThunk(
  'domainControl/get',
  async (_, { dispatch, rejectWithValue }) => {
    const [success, response] = await getDomainControlsList()

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(response)
    }
    return response
  }
)

export const createDomainControl = createAsyncThunk(
  'domainControl/create',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const state = getState()

    const domain = domainSelector(state)
    const verificationMethod = verificationMethodSelector(state)

    const [success, response] = await postDomainControl(verificationMethod, domain)

    if (!success) {
      return rejectWithValue(response)
    }

    dispatch(showSuccess(I18n.t('publisher.organization_settings.domain_control.create.success', { domain })))
    return response
  }
)

export const deleteDomainControl = createAsyncThunk(
  'domainControl/delete',
  async (domainControl, { dispatch, rejectWithValue }) => {
    const [success, response] = await destroyDomainControl(domainControl)

    if (!success) {
      dispatch(showHttpError({ message: I18n.t('errors.server.generic') }))
      return rejectWithValue(response)
    }

    dispatch(showSuccess(I18n.t('publisher.organization_settings.domain_control.toasts.delete_success')))
    return response
  }
)

export const verifyDomainControl = createAsyncThunk(
  'domainControl/verify',
  async (domainControl, { dispatch, rejectWithValue }) => {
    const [[success, response]] = await Promise.all([
      putVerifyDomainControl(domainControl),
      new Promise((resolve) => setTimeout(resolve, VERIFY_DOMAIN_DEBOUNCE_MSEC))
    ])

    if (!success) {
      if (response.type === 'server') {
        dispatch(showHttpError(response))
      }

      return rejectWithValue(response)
    }

    return response
  }
)

export const getDomainControlSummary = createAsyncThunk(
  'domainControl/summary',
  async (domainControl, { rejectWithValue }) => {
    const [success, response] = await getDomainSummary(domainControl)

    if (!success) {
      return rejectWithValue(response)
    }
    return response
  }
)

export const patchDomainControl = createAsyncThunk(
  'domainControl/patch',
  async ({ uuid, data }, { dispatch, rejectWithValue }) => {
    const [success, response] = await patchDomainControlRequest(uuid, data)

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(response)
    }

    return response
  }
)

export const getDomainControlSettingForOrg = createAsyncThunk(
  'domainControlSetting/get',
  async (_, { rejectWithValue }) => {
    const [success, response] = await getDomainControlSetting()

    if (!success) {
      return rejectWithValue(response)
    }
    return response
  }
)

export const updateDomainControlSetting = createAsyncThunk(
  'domainControlSetting/update',
  async ({ accessOwnerType, accessOwnerValue }, { dispatch, rejectWithValue }) => {
    const [success, response] = await patchDomainControlSetting(accessOwnerType, accessOwnerValue)

    if (!success) {
      dispatch(
        showHttpError({
          message: I18n.t('publisher.organization_settings.domain_control.toasts.domain_control_settings_update_failure')
        })
      )
      return rejectWithValue(response)
    }

    dispatch(showSuccess(I18n.t('publisher.organization_settings.domain_control.toasts.domain_control_settings_update_success')))
    return response
  }
)

export const getOrganizationAdminsAndOwner = createAsyncThunk(
  'domainControlSettings/getOrgAdminsForAccessOwner',
  async (_, { dispatch, rejectWithValue }) => {
    const [success, response] = await getOrganizationMemberships()

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(response)
    }

    return response
  }
)

export const postDomainControlExport = createAsyncThunk(
  'domainControl/export',
  async (domainControl, { dispatch, rejectWithValue }) => {
    const [success, response] = await postExport(domainControl)

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(response)
    }

    dispatch(showSuccess(I18n.t('publisher.organization_settings.domain_control.cards.verified.export_success')))

    return response
  }
)
